import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  type QuerySnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import type { User } from '../types';

/**
 * Jobs service — persists grading/generation runs and uploaded source files.
 *
 * Storage layout (owner-only, enforced by Storage rules):
 *   users/{uid}/uploads/{jobId}/{fileName}     ← raw .docx/.pdf we graded
 *
 * Firestore layout (owner-only, enforced by Firestore rules):
 *   users/{uid}/jobs/{jobId}                   ← one doc per run
 *
 * We store metadata, NOT the full AI response, to keep docs <1 MiB and the
 * History page cheap to query. If a user wants the full result JSON, we can
 * re-render from inputs or stash a separate doc in /jobs/{id}/result if
 * needed later.
 */

export type JobKind = 'evaluation' | 'rubric' | 'generation';

export interface JobBase {
  kind: JobKind;
}
export interface EvaluationJob extends JobBase {
  kind: 'evaluation';
  fileName: string;
  storagePath?: string;
  score: number;
  grade: string;
}
export interface RubricJob extends JobBase {
  kind: 'rubric';
  fileName: string;
  storagePath?: string;
  rubricName: string;
  score: number;
  grade: string;
}
export interface GenerationJob extends JobBase {
  kind: 'generation';
  title: string;
  words: number;
  /**
   * Full generated markdown — persisted so the user can re-download the report
   * later from the History page. Kept for 24 h via a Firestore TTL policy on
   * the `expiresAt` field; see docs/firestore-ttl.md for the one-time console
   * setup. Size is bounded by the backend's generation length cap (≤5k words,
   * well under Firestore's 1 MiB document limit).
   */
  markdown?: string;
}
export type JobRecord = EvaluationJob | RubricJob | GenerationJob;

export interface StoredJob extends JobBase {
  id: string;
  createdAt: string;
  fileName?: string;
  storagePath?: string;
  title?: string;
  score?: number;
  grade?: string;
  words?: number;
  rubricName?: string;
  /** Generated markdown (generation jobs only, cleared after 24 h). */
  markdown?: string;
  /** Expiry instant for the markdown retention; ISO string for UI use. */
  expiresAt?: string;
}

/**
 * How long to keep the full generated markdown around, per plan.
 *
 * Rationale: free/try/starter are low-commit tiers so we cap storage cost at
 * 7 days — long enough to cover a weekend + Monday reuse, short enough that we
 * don't accumulate a large backlog of anonymous doc payloads. Pro/Team pay a
 * subscription and expect "my history is there when I come back next month",
 * so we retain for 30 days. Anything longer should be an explicit "save
 * forever" action, not an implicit retention bump.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

export const RETENTION_BY_PLAN: Record<User['plan'], number> = {
  free:    7 * DAY_MS,
  try:     7 * DAY_MS,
  starter: 7 * DAY_MS,
  pro:     30 * DAY_MS,
  team:    30 * DAY_MS,
};

/** Milliseconds of generation-markdown retention for the given plan. */
export function retentionMsForPlan(plan: User['plan'] | undefined): number {
  // Default to the shortest window if plan is missing — better to under-retain
  // than to accidentally hold Pro-length copies for an unauthenticated edge case.
  return RETENTION_BY_PLAN[plan ?? 'free'] ?? RETENTION_BY_PLAN.free;
}

/**
 * @deprecated Retention is now plan-based — use `retentionMsForPlan(plan)`.
 * Kept as an export for any legacy import; resolves to the free-tier window.
 */
export const GENERATION_RETENTION_MS = RETENTION_BY_PLAN.free;

/**
 * Upload the raw source file to Firebase Storage under the current user's
 * namespace. Returns the storage path so we can surface a re-download link later.
 * Failures are non-fatal for the caller — grading still runs without the upload.
 */
export async function uploadReportFile(file: File, jobId: string): Promise<string | undefined> {
  const uid = auth.currentUser?.uid;
  if (!uid) return undefined;
  try {
    const path = `users/${uid}/uploads/${jobId}/${file.name}`;
    const r = ref(storage, path);
    await uploadBytes(r, file, { contentType: file.type || 'application/octet-stream' });
    return path;
  } catch (e) {
    // Don't block the user's run on storage failures — they'll still see results.
    console.warn('[jobsService] upload failed', e);
    return undefined;
  }
}

/**
 * Persist a job record under users/{uid}/jobs. Returns the new doc id.
 *
 * For generation jobs we stamp an `expiresAt` Timestamp so a Firestore TTL
 * policy can auto-delete the doc once the plan-based retention window lapses
 * (7 d for free/try/starter, 30 d for pro/team). Caller passes the user's
 * current plan; we deliberately don't reach into auth state here to keep the
 * service pure and to make the retention decision explicit at the call site.
 * Evaluation/rubric jobs carry no bulky payload and skip expiresAt — they
 * persist indefinitely.
 */
export async function saveJob(job: JobRecord, plan?: User['plan']): Promise<string | null> {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  try {
    const col = collection(db, 'users', uid, 'jobs');
    const extras: Record<string, unknown> = {};
    if (job.kind === 'generation') {
      extras.expiresAt = Timestamp.fromMillis(Date.now() + retentionMsForPlan(plan));
    }
    const docRef = await addDoc(col, {
      ...job,
      ...extras,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (e) {
    console.warn('[jobsService] saveJob failed', e);
    return null;
  }
}

/** Live subscription to the 50 most recent jobs for the signed-in user. */
export function watchJobs(cb: (jobs: StoredJob[]) => void): () => void {
  const uid = auth.currentUser?.uid;
  if (!uid) { cb([]); return () => {}; }
  const q = query(
    collection(db, 'users', uid, 'jobs'),
    orderBy('createdAt', 'desc'),
    limit(50),
  );
  return onSnapshot(q, (snap: QuerySnapshot) => {
    const items: StoredJob[] = snap.docs.map((d) => {
      const data = d.data() as any;
      const createdAt = data.createdAt?.toDate?.() as Date | undefined;
      const expiresAt = data.expiresAt?.toDate?.() as Date | undefined;
      return {
        id: d.id,
        ...data,
        createdAt: createdAt ? createdAt.toISOString() : new Date().toISOString(),
        expiresAt: expiresAt ? expiresAt.toISOString() : undefined,
      };
    });
    cb(items);
  }, (err) => {
    console.warn('[jobsService] watchJobs error', err);
    cb([]);
  });
}
